// src/store/slices/menuItemSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { MenuItem, getBranchMenuItems, selectMenuItemById, insertMenuItem, updateMenuItem, deleteMenuItem, getMenuItemAnalytics } from '../../utils/supabaseClient';
import { Database } from '../../types/supabase';
import { ApiResponse, ArTranslation } from '../../types/custom';
import { setHasChanges } from './branchSlice';

// Cache duration in milliseconds (5 minutes)
const MENU_ITEM_CACHE_DURATION_MS = 5 * 60 * 1000;

export interface MenuItemState {
	menuItems: MenuItem[];
	selectedMenuItem: MenuItem | null;
	filteredItems: MenuItem[];
	searchQuery: string;
	selectedCategoryFilter: string;
	statusFilter: string;
	isLoading: boolean;
	isCreating: boolean;
	isUpdating: boolean;
	isDeleting: boolean;
	error: string | null;
	analytics: { [key: string]: Database['public']['Tables']['item_analytics']['Row'][] };
	isLoadingAnalytics: boolean;
	// Cache metadata
	lastFetchedAt: number | null;
	currentBranchId: string | null;
}

const initialState: MenuItemState = {
	menuItems: [],
	selectedMenuItem: null,
	filteredItems: [],
	searchQuery: '',
	selectedCategoryFilter: '',
	statusFilter: 'all',
	isLoading: false,
	isCreating: false,
	isUpdating: false,
	isDeleting: false,
	error: null,
	analytics: {},
	isLoadingAnalytics: false,
	// Cache metadata
	lastFetchedAt: null,
	currentBranchId: null,
};

// Helper function to filter menu items
const filterMenuItems = (
	items: MenuItem[],
	searchQuery: string,
	categoryFilter: string,
	statusFilter: string
) => {
	return items.filter(item => {
		// Search filter
		const arTranslation = item.ar as ArTranslation | null;
		const matchesSearch = !searchQuery ||
			item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			arTranslation?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			arTranslation?.description?.toLowerCase().includes(searchQuery.toLowerCase());

		// Category filter
		const matchesCategory = !categoryFilter ||
			item.category_id === categoryFilter;

		// Status filter
		let matchesStatus = true;
		switch (statusFilter) {
			case 'published':
				matchesStatus = !!item.is_published;
				break;
			case 'draft':
				matchesStatus = !item.is_published;
				break;
			case 'available':
				matchesStatus = !!item.is_available;
				break;
			case 'unavailable':
				matchesStatus = !item.is_available;
				break;
			case 'featured':
				matchesStatus = !!item.is_featured;
				break;
			case 'all':
			default:
				matchesStatus = true;
				break;
		}

		return matchesSearch && matchesCategory && matchesStatus;
	});
};

// Normalize Supabase RPC responses that may wrap the menu item payload
const normalizeMenuItemPayload = (payload: unknown): MenuItem => {
	if (!payload) {
		throw new Error('No data returned');
	}

	// If the payload is an array, grab the first element recursively
	if (Array.isArray(payload)) {
		if (payload.length === 0) {
			throw new Error('No data returned');
		}
		return normalizeMenuItemPayload(payload[0]);
	}

	if (typeof payload === 'object') {
		const record = payload as Record<string, unknown>;

		if ('data' in record && record.data) {
			return normalizeMenuItemPayload(record.data);
		}

		if ('item' in record && record.item) {
			return normalizeMenuItemPayload(record.item);
		}

		const nestedWithId = Object.values(record).find((value) => {
			if (!value || typeof value !== 'object') {
				return false;
			}

			return 'id' in (value as Record<string, unknown>);
		});

		if (nestedWithId) {
			return normalizeMenuItemPayload(nestedWithId);
		}

		if ('id' in record) {
			return record as MenuItem;
		}
	}

	throw new Error('Unexpected menu item response format');
};

// Async thunks
export const fetchMenuItems = createAsyncThunk(
	'menuItem/fetchMenuItems',
	async (params: { branchId: string; forceRefresh?: boolean }, { getState }) => {
		const { branchId, forceRefresh = false } = params;
		const state = getState() as { menuItem: MenuItemState };
		const { lastFetchedAt, currentBranchId, menuItems } = state.menuItem;

		// Check if we can use cached data
		const now = Date.now();
		const isSameBranch = currentBranchId === branchId;
		const isCacheValid = lastFetchedAt && (now - lastFetchedAt) < MENU_ITEM_CACHE_DURATION_MS;
		const cacheAgeSeconds = lastFetchedAt ? Math.round((now - lastFetchedAt) / 1000) : null;

		if (!forceRefresh && isSameBranch && isCacheValid && menuItems.length > 0) {
			// Return cached data - no API call needed
			console.log(`📦 [MenuItems] Using CACHED data (${menuItems.length} items, ${cacheAgeSeconds}s old)`);
			return { data: menuItems, fromCache: true, branchId };
		}

		// Fetch fresh data from API
		console.log(`🔄 [MenuItems] Fetching FRESH data from API...`);
		const { data, error } = await getBranchMenuItems(branchId);
		if (error) throw error;
		console.log(`✅ [MenuItems] Loaded ${data?.length || 0} items from API`);
		return { data: data || [], fromCache: false, branchId };
	}
);

export const fetchMenuItemById = createAsyncThunk(
	'menuItem/fetchMenuItemById',
	async (itemId: string) => {
		const { data, error } = await selectMenuItemById(itemId);
		if (error) throw error;
		return data;
	}
);

export const fetchMenuItemAnalytics = createAsyncThunk(
	'menuItem/fetchMenuItemAnalytics',
	async (params: { itemId: string; branchId: string }) => {
		const { data, error } = await getMenuItemAnalytics(params.itemId, params.branchId);
		if (error) throw error;
		return data || [];
	}
);

export const createNewMenuItem = createAsyncThunk(
	'menuItem/createNewMenuItem',
	async (menuItemData: Database["public"]["Functions"]["insert_item"]["Args"]["payload"], { dispatch }) => {
		const result = await insertMenuItem(menuItemData);
		if (!result) throw new Error('Null result');
		
		// Handle new response format: { data: "item-id", success: true }
		const typedResult = result as unknown as { data: string | MenuItem; success: boolean; message?: string };
		if (!typedResult.success) throw new Error(typedResult.message || 'Failed to create item');
		if (!typedResult.data) throw new Error('No data returned');
		
		// If data is just an ID string, construct the MenuItem from the payload
		let menuItem: MenuItem;
		if (typeof typedResult.data === 'string') {
			// Construct MenuItem from payload + returned ID
			const payload = menuItemData as any;
			menuItem = {
				id: typedResult.data,
				name: payload.name,
				description: payload.description || null,
				price: payload.price,
				discounted_price: payload.discounted_price || null,
				category_id: payload.category_id,
				branch_id: payload.branch_id,
				image_url: payload.image_url || null,
				is_published: payload.is_published ?? true,
				is_available: payload.is_available ?? true,
				is_featured: payload.is_featured ?? false,
				spice_level: payload.spice_level || null,
				calories: payload.calories || null,
				preparation_time: payload.preparation_time || null,
				ar: payload.ar || null,
				tags: payload.tags || [],
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
			} as MenuItem;
		} else {
			// Legacy format: full MenuItem object returned
			menuItem = typedResult.data as MenuItem;
		}

		const normalized = normalizeMenuItemPayload(menuItem);
		dispatch(setHasChanges(true));
		return normalized;
	}
);

export const updateMenuItemData = createAsyncThunk(
	'menuItem/updateMenuItemData',
	async (menuItemData: Database["public"]["Functions"]["update_item"]["Args"]["payload"], { dispatch }) => {
		const result = await updateMenuItem(menuItemData);
		if (!result) throw new Error('Null result')
		const typedResult = result as unknown as ApiResponse<MenuItem>;
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		const normalized = normalizeMenuItemPayload(typedResult.data);
		dispatch(setHasChanges(true));
		return normalized;
	}
);

export const deleteMenuItemData = createAsyncThunk(
	'menuItem/deleteMenuItemData',
	async (itemId: string, { dispatch }) => {
		const result = await deleteMenuItem(itemId);
		if (!result) throw new Error('Null result')
		const typedResult = result as unknown as ApiResponse<MenuItem>;
		if (!typedResult.success) throw new Error(typedResult.message)
		if (!typedResult.data) throw new Error('No data returned');

		const normalized = normalizeMenuItemPayload(typedResult.data);
		dispatch(setHasChanges(true));
		return normalized;
	}
);

const menuItemSlice = createSlice({
	name: 'menuItem',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		setSelectedMenuItem: (state, action: PayloadAction<MenuItem | null>) => {
			// @ts-ignore - Avoiding Redux Toolkit deep instantiation issue
			state.selectedMenuItem = action.payload;
		},
		clearMenuItems: (state) => {
			state.menuItems = [];
			state.filteredItems = [];
			state.selectedMenuItem = null;
			// Clear cache metadata
			state.lastFetchedAt = null;
			state.currentBranchId = null;
		},
		invalidateMenuItemCache: (state) => {
			// Invalidate cache to force refresh on next fetch
			state.lastFetchedAt = null;
		},
		setSearchQuery: (state, action: PayloadAction<string>) => {
			state.searchQuery = action.payload;
			state.filteredItems = filterMenuItems(
				state.menuItems,
				action.payload,
				state.selectedCategoryFilter,
				state.statusFilter
			);
		},
		setCategoryFilter: (state, action: PayloadAction<string>) => {
			state.selectedCategoryFilter = action.payload;
			state.filteredItems = filterMenuItems(
				state.menuItems,
				state.searchQuery,
				action.payload,
				state.statusFilter
			);
		},
		setStatusFilter: (state, action: PayloadAction<string>) => {
			state.statusFilter = action.payload;
			state.filteredItems = filterMenuItems(
				state.menuItems,
				state.searchQuery,
				state.selectedCategoryFilter,
				action.payload
			);
		},
		clearFilters: (state) => {
			state.searchQuery = '';
			state.selectedCategoryFilter = '';
			state.statusFilter = 'all';
			state.filteredItems = state.menuItems;
		},
		toggleMenuItemPublishStatus: (state, action: PayloadAction<string>) => {
			const itemId = action.payload;
			const item = state.menuItems.find(item => item.id === itemId);
			if (item) {
				item.is_published = !item.is_published;

				// Update selected item if it matches
				if (state.selectedMenuItem?.id === itemId) {
					state.selectedMenuItem.is_published = item.is_published;
				}

				// Update filtered items
				state.filteredItems = filterMenuItems(
					state.menuItems,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);
			}
		},
		toggleMenuItemAvailability: (state, action: PayloadAction<string>) => {
			const itemId = action.payload;
			const item = state.menuItems.find(item => item.id === itemId);
			if (item) {
				item.is_available = !item.is_available;

				// Update selected item if it matches
				if (state.selectedMenuItem?.id === itemId) {
					state.selectedMenuItem.is_available = item.is_available;
				}

				// Update filtered items
				state.filteredItems = filterMenuItems(
					state.menuItems,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);
			}
		},
		toggleMenuItemFeatured: (state, action: PayloadAction<string>) => {
			const itemId = action.payload;
			const item = state.menuItems.find(item => item.id === itemId);
			if (item) {
				item.is_featured = !item.is_featured;

				// Update selected item if it matches
				if (state.selectedMenuItem?.id === itemId) {
					state.selectedMenuItem.is_featured = item.is_featured;
				}

				// Update filtered items
				state.filteredItems = filterMenuItems(
					state.menuItems,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);
			}
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch menu items
			.addCase(fetchMenuItems.pending, (state) => {
				state.isLoading = state.menuItems.length === 0;
				state.error = null;
			})
			.addCase(fetchMenuItems.fulfilled, (state, action) => {
				state.isLoading = false;
				const { data, fromCache, branchId } = action.payload;

				// Only update cache metadata if we fetched fresh data
				if (!fromCache) {
					state.lastFetchedAt = Date.now();
					state.currentBranchId = branchId;
				}

				state.menuItems = data;
				state.filteredItems = filterMenuItems(
					data,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);
				state.error = null;
			})
			.addCase(fetchMenuItems.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to fetch menu items';
			})
			// Fetch menu item analytics
			.addCase(fetchMenuItemAnalytics.pending, (state) => {
				state.isLoadingAnalytics = true;
				state.error = null;
			})
			.addCase(fetchMenuItemAnalytics.fulfilled, (state, action) => {
				state.isLoadingAnalytics = false;
				// console.log('item_id', action.meta.arg);
				state.analytics = { ...state.analytics, [action.meta.arg.itemId]: action.payload };
				state.error = null;
			})
			.addCase(fetchMenuItemAnalytics.rejected, (state, action) => {
				state.isLoadingAnalytics = false;
				state.error = action.error.message || 'Failed to fetch menu item analytics';
			})
			// Fetch menu item by ID
			.addCase(fetchMenuItemById.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchMenuItemById.fulfilled, (state, action) => {
				state.isLoading = false;
				state.selectedMenuItem = action.payload;
				state.error = null;
			})
			.addCase(fetchMenuItemById.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to fetch menu item';
			})
			// Create menu item
			.addCase(createNewMenuItem.pending, (state) => {
				state.isCreating = true;
				state.error = null;
			})
			.addCase(createNewMenuItem.fulfilled, (state, action) => {
				state.isCreating = false;
				state.menuItems = [action.payload, ...state.menuItems];
				state.filteredItems = filterMenuItems(
					state.menuItems,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);
				state.error = null;
			})
			.addCase(createNewMenuItem.rejected, (state, action) => {
				state.isCreating = false;
				state.error = action.error.message || 'Failed to create menu item';
			})
			// Update menu item
			.addCase(updateMenuItemData.pending, (state) => {
				state.isUpdating = true;
				state.error = null;
			})
			.addCase(updateMenuItemData.fulfilled, (state, action) => {
				state.isUpdating = false;
				const updatedItem = action.payload;

				// Update in main array
				const index = state.menuItems.findIndex(item => item.id === updatedItem.id);
				if (index !== -1) {
					state.menuItems[index] = updatedItem;
				}

				// Update selected item if it matches
				if (state.selectedMenuItem?.id === updatedItem.id) {
					state.selectedMenuItem = updatedItem;
				}

				// Update filtered items
				state.filteredItems = filterMenuItems(
					state.menuItems,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);

				state.error = null;
			})
			.addCase(updateMenuItemData.rejected, (state, action) => {
				state.isUpdating = false;
				state.error = action.error.message || 'Failed to update menu item';
			})
			// Delete menu item
			.addCase(deleteMenuItemData.pending, (state) => {
				state.isDeleting = true;
				state.error = null;
			})
			.addCase(deleteMenuItemData.fulfilled, (state, action) => {
				state.isDeleting = false;
				const deletedItemId = action.payload.id;

				// Remove from main array
				state.menuItems = state.menuItems.filter(item => item.id !== deletedItemId);

				// Clear selected item if it was deleted
				if (state.selectedMenuItem?.id === deletedItemId) {
					state.selectedMenuItem = null;
				}

				// Update filtered items
				state.filteredItems = filterMenuItems(
					state.menuItems,
					state.searchQuery,
					state.selectedCategoryFilter,
					state.statusFilter
				);

				state.error = null;
			})
			.addCase(deleteMenuItemData.rejected, (state, action) => {
				state.isDeleting = false;
				state.error = action.error.message || 'Failed to delete menu item';
			});
	},
});

export const {
	clearError,
	setSelectedMenuItem,
	clearMenuItems,
	invalidateMenuItemCache,
	setSearchQuery,
	setCategoryFilter,
	setStatusFilter,
	clearFilters,
	toggleMenuItemPublishStatus,
	toggleMenuItemAvailability,
	toggleMenuItemFeatured,
} = menuItemSlice.actions;

export default menuItemSlice.reducer;